import { Link } from "react-router-dom";
import { LegalLayout, LegalSection, ContactEmail } from "../../components/LegalLayout";

const policyLink = "font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service | EnquiryOps"
      description="Terms governing use of the EnquiryOps platform for enrolment and enquiry management."
      heading="Terms of Service"
      updated="19 July 2026"
    >
      <LegalSection title="Acceptance of Terms">
        <p>
          By creating an account, placing a deposit, or using the EnquiryOps website or platform, you agree to
          these Terms of Service. If you are using EnquiryOps on behalf of a business, you confirm you have
          authority to bind that business to these terms.
        </p>
      </LegalSection>

      <LegalSection title="Description of Service">
        <p>
          EnquiryOps is an enquiry and enrolment management platform for education and enrichment businesses. It
          helps centres respond to parent enquiries, coordinate trial bookings, run follow-up workflows, and
          track conversion performance. Features may change as the product develops.
        </p>
      </LegalSection>

      <LegalSection title="Account Registration and Responsibilities">
        <p>
          You are responsible for the accuracy of the information you provide, for keeping your login
          credentials secure, and for all activity under your account. Notify us promptly at <ContactEmail email="privacy@getenquiryops.com" /> if
          you suspect unauthorised use of your account.
        </p>
      </LegalSection>

      <LegalSection title="Subscription, Fees, and Payment">
        <p>
          Fees for the founding cohort and standard plans are stated on our pricing page. The founding deposit
          is fully refundable as described at the point of payment: it is credited toward setup or your first
          month if you proceed, and returned in full if either side decides EnquiryOps is not a fit. Payments
          are processed by Stripe. Subscription fees are billed in advance and are non-refundable except as
          stated in these terms or required by law.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable Use">
        <p>
          You agree not to misuse the service, including sending unsolicited bulk messages, uploading unlawful
          content, attempting to breach security, or interfering with other users. All messaging through
          EnquiryOps must comply with our <Link to="/anti-spam-policy" className={policyLink} data-testid="terms-antispam-link">Anti-Spam Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="Intellectual Property">
        <p>
          EnquiryOps and its software, design, and content remain our property. You retain ownership of the data
          you and your customers submit to the platform, and you grant us the licence needed to operate the
          service on your behalf.
        </p>
      </LegalSection>

      <LegalSection title="Data and Privacy">
        <p>
          Our collection and use of personal data is governed by our <Link to="/privacy-policy" className={policyLink} data-testid="terms-privacy-link">Privacy Policy</Link>.
          Where you use EnquiryOps to process your customers' personal data, you are responsible for having the
          right to do so, and we process that data on your instructions.
        </p>
      </LegalSection>

      <LegalSection title="Termination">
        <p>
          You may cancel your subscription at any time, effective at the end of the current billing period. We
          may suspend or terminate access for breach of these terms, including violations of the Acceptable Use
          section, with immediate effect for serious violations. On termination, we will make your data
          available for export for a reasonable period before deletion.
        </p>
      </LegalSection>

      <LegalSection title="Disclaimers and Limitation of Liability">
        <p>
          The service is provided on an "as is" and "as available" basis. To the maximum extent permitted by
          law, we disclaim implied warranties and are not liable for indirect, incidental, or consequential
          losses. Our total liability for any claim is limited to the fees you paid to us in the 12 months
          before the claim arose. Nothing in these terms excludes liability that cannot be excluded by law.
        </p>
      </LegalSection>

      <LegalSection title="Governing Law">
        <p>
          These terms are governed by the laws of Singapore, and the courts of Singapore have exclusive
          jurisdiction over any dispute arising from them.
        </p>
      </LegalSection>

      <LegalSection title="Changes to These Terms">
        <p>
          We may update these terms from time to time. Material changes will be posted on this page with an
          updated date, and continued use of the service after changes take effect means you accept the updated
          terms.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about these terms: <ContactEmail email="privacy@getenquiryops.com" />.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
